/** Host OS — Schedule. Day / Week / Month views of everything you run.
 *  Week is the default, with hourly rows so owners can see timings and overlaps. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { eventCalendarDate } from "@/lib/dates";
import HostLayout from "@/components/host/HostLayout";
import CreateEventPicker from "@/components/CreateEventPicker";
import { HostPageHeader } from "@/components/host/hostUi";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOUR_H = 52; // px per hour row
const TYPE_DOT = { game: "bg-blue-500", series: "bg-fuchsia-500", trip: "bg-amber-500" };
const TYPE_BLOCK = {
  game: "bg-blue-500/15 border-l-2 border-blue-500 text-blue-900 dark:text-blue-200",
  series: "bg-fuchsia-500/15 border-l-2 border-fuchsia-500 text-fuchsia-900 dark:text-fuchsia-200",
  trip: "bg-amber-500/15 border-l-2 border-amber-500 text-amber-900 dark:text-amber-200",
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -((d.getDay() + 6) % 7)); // Monday
const toMin = (t) => {
  if (!t || typeof t !== "string") return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

// Assign overlapping events to side-by-side lanes within a single day.
const layoutDay = (evs) => {
  const sorted = [...evs].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const out = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const laneEnds = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < laneEnds.length; i++) {
        if (ev.startMin >= laneEnds[i]) { ev.lane = i; laneEnds[i] = ev.endMin; placed = true; break; }
      }
      if (!placed) { ev.lane = laneEnds.length; laneEnds.push(ev.endMin); }
    });
    cluster.forEach((ev) => { ev.lanes = laneEnds.length; });
    out.push(...cluster);
    cluster = []; clusterEnd = -1;
  };
  sorted.forEach((ev) => {
    if (cluster.length && ev.startMin >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.endMin);
  });
  if (cluster.length) flush();
  return out;
};

const HostSchedulePage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("week"); // day | week | month
  const [cursor, setCursor] = useState(() => new Date());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragCtx, setDragCtx] = useState(null);
  const [sel, setSel] = useState(null); // { key, a, b } minutes while dragging
  const selRef = useRef(null);
  const dragging = useRef(false);
  useEffect(() => { selRef.current = sel; }, [sel]);

  // Finalise a drag anywhere on the page → open the picker prefilled with the slot
  useEffect(() => {
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      const s = selRef.current;
      setSel(null);
      if (!s) return;
      const start = Math.min(s.a, s.b);
      let end = Math.max(s.a, s.b);
      if (end - start < 30) end = start + 60; // a simple click = 1h slot
      setDragCtx({ date: s.key, start: fmtMin(start), end: fmtMin(end) });
      setPickerOpen(true);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/events?status=all&type=all`, { headers });
      setEvents(res.ok ? await res.json() : []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // yyyy-mm-dd -> [events], each carrying parsed start/end minutes
  const byDay = useMemo(() => {
    const m = {};
    events.forEach((ev) => {
      const d = eventCalendarDate(ev.date);
      if (!d) return;
      const startMin = toMin(ev.time_start);
      const endMin = toMin(ev.time_end);
      const item = {
        ...ev,
        startMin: startMin ?? null,
        endMin: endMin != null && startMin != null && endMin > startMin ? endMin : (startMin != null ? startMin + 60 : null),
      };
      const key = ymd(d);
      (m[key] = m[key] || []).push(item);
    });
    return m;
  }, [events]);

  const days = view === "day" ? [cursor] : view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i)) : [];

  // Dynamic hour range for day/week so the grid is compact but shows outliers.
  const [startHour, endHour] = useMemo(() => {
    let min = 8, max = 21;
    days.forEach((d) => {
      (byDay[ymd(d)] || []).forEach((ev) => {
        if (ev.startMin == null) return;
        min = Math.min(min, Math.floor(ev.startMin / 60));
        max = Math.max(max, Math.ceil((ev.endMin ?? ev.startMin + 60) / 60));
      });
    });
    return [Math.max(0, min), Math.min(24, max)];
  }, [byDay, days]);

  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const todayKey = ymd(new Date());

  const label = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(cursor), e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${e.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [view, cursor]);

  const move = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addDays(cursor, dir * 7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
  };

  // Month grid (6 weeks from Monday)
  const monthGrid = useMemo(() => {
    if (view !== "month") return [];
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [view, cursor]);

  const openEvent = (ev) => navigate(`/host/manage/${ev.type}/${ev.id}`);

  const minFromEvent = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    let m = startHour * 60 + ((e.clientY - rect.top) / HOUR_H) * 60;
    m = Math.round(m / 15) * 15;
    return Math.max(startHour * 60, Math.min(endHour * 60, m));
  };
  const onColDown = (key, e) => {
    if (e.button !== 0) return;
    dragging.current = true;
    const m = minFromEvent(e);
    setSel({ key, a: m, b: m + 60 });
  };
  const onColMove = (key, e) => {
    if (!dragging.current || !selRef.current || selRef.current.key !== key) return;
    const m = minFromEvent(e);
    setSel((s) => (s ? { ...s, b: m } : s));
  };

  return (
    <HostLayout>
      <div data-testid="host-schedule-page">
        <HostPageHeader
          icon={CalendarClock}
          title="Schedule"
          subtitle="Every session, course and retreat you run — see timings and overlaps at a glance."
          actions={
            <Button onClick={() => setPickerOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" size="sm" data-testid="schedule-create">
              <Plus className="w-4 h-4" /> Add offering
            </Button>
          }
        />

        {/* Toolbar: view switch + navigation */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40" data-testid="schedule-view-switch">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm rounded-md capitalize transition-colors ${view === v ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`schedule-view-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid="cal-prev" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid="cal-next" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <span className="ml-2 font-display text-lg" data-testid="schedule-range-label">{label}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : view === "month" ? (
          <Card className="p-4" data-testid="schedule-month">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-[10px] uppercase tracking-wider text-muted-foreground py-1">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthGrid.map((d) => {
                const key = ymd(d);
                const inMonth = d.getMonth() === cursor.getMonth();
                const dayEvents = (byDay[key] || []).slice().sort((a, b) => (a.startMin ?? 9999) - (b.startMin ?? 9999));
                const isToday = key === todayKey;
                return (
                  <div
                    key={key}
                    onClick={() => { setDragCtx({ date: key }); setPickerOpen(true); }}
                    className={`min-h-[76px] rounded-lg border p-1.5 flex flex-col gap-1 cursor-pointer hover:border-primary/50 transition-colors ${inMonth ? "border-border bg-background" : "border-transparent bg-muted/20"} ${isToday ? "ring-1 ring-primary" : ""}`}
                    data-testid={`cal-day-${key}`}
                  >
                    <span className={`text-[11px] font-medium ${inMonth ? "text-foreground" : "text-muted-foreground/50"} ${isToday ? "text-primary-ink" : ""}`}>{d.getDate()}</span>
                    <div className="flex flex-col gap-0.5 overflow-hidden">
                      {dayEvents.slice(0, 3).map((ev) => (
                        <button key={`${ev.type}-${ev.id}`} onClick={(e) => { e.stopPropagation(); openEvent(ev); }} className="flex items-center gap-1 text-left group" title={ev.title} data-testid={`cal-event-${ev.id}`}>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TYPE_DOT[ev.type] || "bg-muted-foreground"}`} />
                          <span className="text-[10px] truncate group-hover:text-primary-ink">{ev.time_start ? `${ev.time_start} ` : ""}{ev.title}</span>
                        </button>
                      ))}
                      {dayEvents.length > 3 && <span className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <Legend />
          </Card>
        ) : (
          // DAY / WEEK — hourly grid, styled to match the month calendar
          <Card className="p-4" data-testid={`schedule-${view}`}>
            <div className="rounded-xl border border-border overflow-hidden">
              {/* Day headers */}
              <div className="grid bg-muted/30 border-b border-border" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
                <div className="border-r border-border" />
                {days.map((d) => {
                  const isToday = ymd(d) === todayKey;
                  const allDay = (byDay[ymd(d)] || []).filter((ev) => ev.startMin == null);
                  return (
                    <div key={ymd(d)} className="border-r border-border last:border-r-0 px-2 py-2 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                      <div className="mt-0.5 flex justify-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-display ${isToday ? "bg-primary text-primary-foreground" : "text-foreground"}`}>{d.getDate()}</span>
                      </div>
                      {allDay.map((ev) => (
                        <button key={`${ev.type}-${ev.id}`} onClick={() => openEvent(ev)} className={`mt-1 w-full truncate rounded-md px-1.5 py-0.5 text-[10px] ${TYPE_BLOCK[ev.type] || "bg-muted"}`} title={ev.title} data-testid={`allday-event-${ev.id}`}>
                          {ev.title}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Hour grid */}
              <div className="overflow-y-auto bg-background" style={{ maxHeight: "64vh" }}>
                <div className="grid relative" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
                  {/* Hour gutter */}
                  <div className="relative">
                    {hours.map((h) => (
                      <div key={h} className="relative border-t border-r border-border/60" style={{ height: HOUR_H }}>
                        {h !== startHour && (
                          <span className="absolute right-1.5 -top-1.5 text-[10px] text-muted-foreground">{String(h).padStart(2, "0")}:00</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {days.map((d) => {
                    const key = ymd(d);
                    const timed = layoutDay((byDay[key] || []).filter((ev) => ev.startMin != null));
                    const isToday = key === todayKey;
                    return (
                      <div
                        key={key}
                        onMouseDown={(e) => onColDown(key, e)}
                        onMouseMove={(e) => onColMove(key, e)}
                        className={`relative border-r border-border/60 last:border-r-0 select-none cursor-pointer hover:bg-muted/10 ${isToday ? "bg-primary/5" : ""}`}
                        data-testid={`schedule-col-${key}`}
                      >
                        {hours.map((h) => (
                          <div key={h} className="border-t border-border/50" style={{ height: HOUR_H }} />
                        ))}

                        {/* Drag selection preview */}
                        {sel && sel.key === key && (() => {
                          const a = Math.min(sel.a, sel.b), b = Math.max(sel.a, sel.b);
                          const top = ((a - startHour * 60) / 60) * HOUR_H;
                          const height = Math.max(14, ((b - a) / 60) * HOUR_H);
                          return (
                            <div className="absolute left-1 right-1 rounded-lg bg-primary/20 border border-dashed border-primary pointer-events-none z-10 p-1" style={{ top, height }} data-testid="schedule-drag-preview">
                              <span className="text-[10px] font-medium text-primary-ink">{fmtMin(a)}–{fmtMin(b)}</span>
                            </div>
                          );
                        })()}

                        {timed.map((ev) => {
                          const top = ((ev.startMin - startHour * 60) / 60) * HOUR_H;
                          const height = Math.max(22, ((ev.endMin - ev.startMin) / 60) * HOUR_H - 3);
                          const widthPct = 100 / ev.lanes;
                          return (
                            <button
                              key={`${ev.type}-${ev.id}`}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => openEvent(ev)}
                              className={`absolute rounded-lg shadow-sm px-1.5 py-1 text-left overflow-hidden hover:shadow transition-shadow ${TYPE_BLOCK[ev.type] || "bg-muted"}`}
                              style={{ top, height, left: `calc(${ev.lane * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`, zIndex: 5 }}
                              title={`${ev.title} · ${ev.time_start}–${ev.time_end || ""}`}
                              data-testid={`timed-event-${ev.id}`}
                            >
                              <span className="block text-[11px] font-medium leading-tight truncate">{ev.title}</span>
                              <span className="block text-[10px] opacity-80 leading-tight">{ev.time_start}{ev.time_end ? `–${ev.time_end}` : ""}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <Legend />
              <span className="text-[11px] text-muted-foreground hidden sm:block">Tip: click or drag on the grid to add an offering</span>
            </div>
          </Card>
        )}
      </div>
      <CreateEventPicker
        open={pickerOpen}
        onOpenChange={(o) => { setPickerOpen(o); if (!o) setDragCtx(null); }}
        prefill={dragCtx}
        fromHost
      />
    </HostLayout>
  );
};

const Legend = () => (
  <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Sessions</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-fuchsia-500" /> Series</span>
    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Retreats</span>
  </div>
);

export default HostSchedulePage;
